/**
 * v1.1 AI 周报页 /ai-reports
 *
 * 个人列表（按时间倒序）+ 详情
 * 手动触发：生成上周报告
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { $ } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { fmtDateShort, fmtRelative } from '../lib/format';
import { PageHeader, HomeCard, Btn, Pill } from '../components/ui';

interface Report {
  id: number;
  type: string;
  userId?: number;
  periodStart: string;
  periodEnd: string;
  aiInsights: any;
  score?: number;
  generatedAt: string;
}

export function AiReportsPage() {
  const me = useAuthStore((s) => s.user)!;
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<number | null>(null);
  const { data: reports } = useQuery<Report[]>({
    queryKey: ['ai-reports-my'],
    queryFn: () => $.get('/ai/reports/my'),
  });
  const generate = useMutation({
    mutationFn: () => $.post('/ai/reports/personal/generate', { week_offset: 1 }),
    onSuccess: () => {
      message.success('已生成上周报告');
      qc.invalidateQueries({ queryKey: ['ai-reports-my'] });
    },
    onError: (e: any) => message.error(e?.message ?? '生成失败'),
  });
  const active = (reports ?? []).find((r) => r.id === activeId) ?? (reports ?? [])[0];

  return (
    <>
      <PageHeader
        breadcrumb={['AI', '复盘报告']}
        title="📊 AI 周报"
        subtitle={`${me.realName} · 按周生成的 AI 复盘`}
        actions={
          <Btn kind="primary" onClick={() => generate.mutate()} disabled={generate.isPending}>
            {generate.isPending ? 'AI 生成中（5-10 秒）…' : '✨ 生成上周报告'}
          </Btn>
        }
      />
      <div className="work-content">
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16 }}>
          <HomeCard title={`历史报告（${reports?.length ?? 0}）`}>
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {(reports ?? []).length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
                  暂无报告<br/>点上方"✨ 生成上周报告"
                </div>
              )}
              {(reports ?? []).map((r) => {
                const isActive = active?.id === r.id;
                return (
                  <div key={r.id} onClick={() => setActiveId(r.id)}
                       style={{
                         padding: 12, borderBottom: '1px solid var(--border-default)',
                         cursor: 'pointer',
                         background: isActive ? 'var(--primary-bg)' : 'transparent',
                         borderLeft: isActive ? '3px solid var(--primary)' : '3px solid transparent',
                       }}>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                      {fmtDateShort(r.periodStart)} - {fmtDateShort(r.periodEnd)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Pill tone={(r.score ?? 0) >= 70 ? 'success' : (r.score ?? 0) >= 50 ? 'warning' : 'default'}>
                        {r.score ?? '—'} 分
                      </Pill>
                      <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                        {fmtRelative(r.generatedAt)}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4,
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.aiInsights?.summary ?? ''}
                    </div>
                  </div>
                );
              })}
            </div>
          </HomeCard>
          {active ? <ReportDetail report={active} /> : (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>
              选择左侧报告查看详情
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function ReportDetail({ report }: { report: Report }) {
  const ai = report.aiInsights ?? {};
  return (
    <HomeCard title={`${fmtDateShort(report.periodStart)} - ${fmtDateShort(report.periodEnd)} 复盘`}>
      <div style={{ padding: 20 }}>
        <div style={{ fontSize: 14, color: 'var(--text-1)', lineHeight: 1.6, marginBottom: 16 }}>
          {ai.summary ?? '（无摘要）'}
        </div>
        {Array.isArray(ai.strengths) && ai.strengths.length > 0 && (
          <Section title="✅ 强项">
            {ai.strengths.map((s: any, i: number) => (
              <div key={i} style={{ padding: '6px 10px', background: 'rgba(0,180,42,0.08)',
                                    borderRadius: 4, marginBottom: 4, fontSize: 13 }}>{typeof s === 'string' ? s : JSON.stringify(s)}</div>
            ))}
          </Section>
        )}
        {Array.isArray(ai.weaknesses) && ai.weaknesses.length > 0 && (
          <Section title="⚠️ 弱项 + 根因">
            {ai.weaknesses.map((w: any, i: number) => (
              <div key={i} style={{ padding: 10, background: 'rgba(255,125,0,0.06)',
                                    borderLeft: '3px solid var(--warning, #FF7D00)',
                                    borderRadius: 4, marginBottom: 6 }}>
                {typeof w === 'string' ? <div>{w}</div> : (
                  <>
                    {w.issue && <div style={{ fontWeight: 500 }}>{w.issue}</div>}
                    {w.root_cause && <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>{w.root_cause}</div>}
                    {w.evidence && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>证据：{w.evidence}</div>}
                  </>
                )}
              </div>
            ))}
          </Section>
        )}
        {Array.isArray(ai.recommendations) && ai.recommendations.length > 0 && (
          <Section title="💡 行动建议">
            {ai.recommendations.map((r: any, i: number) => (
              <div key={i} style={{ padding: 10, background: 'var(--primary-bg)', borderRadius: 4, marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 500 }}>{i + 1}. {typeof r === 'string' ? r : r.action}</span>
                  {r.priority && (
                    <Pill tone={r.priority === 'high' ? 'danger' : r.priority === 'medium' ? 'warning' : 'default'}>
                      {r.priority}
                    </Pill>
                  )}
                </div>
                {r.expected_impact && (
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>预期：{r.expected_impact}</div>
                )}
              </div>
            ))}
          </Section>
        )}
        {Array.isArray(ai.high_potential_customers) && ai.high_potential_customers.length > 0 && (
          <Section title="🎯 本周高潜客户">
            {ai.high_potential_customers.map((c: any, i: number) => (
              <a key={i} href={`/customers/${c.customer_id}`}
                 style={{ display: 'block', padding: 10, background: 'var(--bg)',
                          borderRadius: 4, marginBottom: 4, textDecoration: 'none', color: 'inherit' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <strong>{c.name ?? `#${c.customer_id}`}</strong>
                  {c['成交概率'] != null && <Pill tone="success">{c['成交概率']}%</Pill>}
                </div>
                {c.why && <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>{c.why}</div>}
              </a>
            ))}
          </Section>
        )}
      </div>
    </HomeCard>
  );
}

function Section({ title, children }: { title: string; children: any }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}
